import { Injectable } from '@angular/core';
import { BaseFormService } from 'ag-common-svc/lib/utils/base-form-service';
import {
  Agent,
  AgentKeys,
  EmailAddressKeys,
  Entity,
  EntityApplications,
  EntityPermissionActivityKeys,
} from '@ag-common-lib/public-api';
import { IdentityApiService } from 'ag-common-svc/lib/services/identity-api.service';
import { shareReplay } from 'rxjs/operators';
import { from, Observable, startWith } from 'rxjs';

@Injectable()
export class ConferencePermissionsModalService extends BaseFormService<Partial<Agent>> {
  /**
   * Agents holding read on this entity, resolved by the identity API.
   *
   * Previously this asked for the role ids and then queried the Firestore agent
   * documents by their `roles` array. Assignments moved to SQL, so that array stopped
   * being written and the list silently went stale — agents newly granted the role
   * never appeared, and agents whose role was removed never left.
   */
  agentListWithConferencePermission$: Observable<Partial<Agent>[]> = from(
    this.identityApi.getAgentsByPermission(
      EntityApplications.crm,
      Entity.conferenceList,
      EntityPermissionActivityKeys.read,
    ),
  ).pipe(startWith([]), shareReplay(1));

  constructor(private identityApi: IdentityApiService) {
    super();
  }

  getFormData = async (data?: Partial<Agent>): Promise<Agent> => {
    const primaryEmailAddress = data?.[AgentKeys.email_addresses] || [
      {
        [EmailAddressKeys.address]: data?.[AgentKeys.p_email],
        [EmailAddressKeys.isLogin]: true,
      },
    ];
    const initialData = Object.assign({}, new Agent(), data, {
      [AgentKeys.email_addresses]: primaryEmailAddress ?? [],
    });

    this.formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];
        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });

    return this.formData;
  };
}
